/**
 * Lazy loader for the vendored `rustfoil-wasm` package (FXC-9009).
 *
 * The package is built with `wasm-pack --target web`, so the JS glue fetches its
 * `.wasm` payload via `new URL('rustfoil_wasm_bg.wasm', import.meta.url)` and must be
 * initialised once (the default export) before any export is callable. We import it
 * dynamically so the ~1 MB wasm only lands when a WebGPU solve is actually requested
 * (the RANS/V100 path never touches it), and memoise the initialised module.
 *
 * Vite handles the `new URL(..., import.meta.url)` asset reference natively for the
 * web target — it rewrites it to a hashed asset URL at build time and serves it in
 * dev — so no `assetsInclude` / `?url` shim is needed here.
 */
import type * as RustfoilWasm from "./pkg/rustfoil_wasm";

type RustfoilWasmModule = typeof RustfoilWasm;

let modPromise: Promise<RustfoilWasmModule> | null = null;

/** Import + initialise the wasm module once; subsequent calls reuse the same instance. */
export async function loadWasm(): Promise<RustfoilWasmModule> {
  if (!modPromise) {
    modPromise = (async () => {
      const mod = await import("./pkg/rustfoil_wasm");
      await mod.default();
      return mod;
    })();
  }
  return modPromise;
}
